import * as React from "react";

import GenButton from "../Gen/Button";
import GenMediaDuplicationTool from "../Gen/MediaDuplicationTool";
import GenMediaUploadButton from "../Gen/MediaUploadButton";
import GenModal from "../Gen/Modal";
import GenS3UploaderButton from "../Gen/S3UploaderButton";

import DocVideoJWPlayer, { SUBTITLE_LANGUAGES } from "./VideoJWPlayer";

import {capitalize} from "inflection";


import {
  setNodeValueByUIDAndKey,
  deleteNodeByUID,
} from "../../src/doc_helpers";

interface DocMediaModalProps {
  currentDocument: CurrentDocument;
  currentVersion: CurrentVersion;
  displayState: DisplayState;
  editState: EditStateTrue;
  currentUser: CurrentUser;
  currentRoleDefinition: CurrentRoleDefinition;
}

export default function DocMediaModal(props: DocMediaModalProps) {
  const {
    currentDocument,
    currentVersion,
    editState,
    displayState,
  } = props;

  const {
    openMenu,
  } = displayState;

  if (!("aws_folder_url" in currentVersion)) { return null; }

  if (!("title" in currentDocument)) { return null; }

  if (!openMenu || openMenu.name !== "MEDIA_MENU") { return null; }

  const {mediaType} = openMenu;

  let renderedMediaItems: React.ReactNode;
  if (mediaType === "video") {
    renderedMediaItems = (currentDocument.media_assets || []).map((mediaItem) => {
      const selectMediaItem = () => {
        currentVersion._actions.modifyEditJSON((json) => setNodeValueByUIDAndKey(json, openMenu.uid, "uri", mediaItem.media_key));
        displayState.setOpenMenu(undefined);
      };

      const videoUrl = `https://d3o9veyy7wy4ai.cloudfront.net/${mediaItem.media_key}`;

      return (
        <div key={mediaItem.media_key}>
          <DocVideoJWPlayer
            videoKey={`media-menu-${mediaItem.media_key}`}
            videoURL={videoUrl}
            hasCaptions={!!mediaItem?.three_play_media_captions_processed_at}
            shouldLoop={mediaItem.is_silent}
            subtitlesByLanguage={mediaItem.subtitle_file_by_language}
          />
          <h6 style={{margin: "5px 0"}}>{`${mediaItem.name} (ID: ${mediaItem.id})`}</h6>
          <div className="flex gap-2 mb-3 items-center">
            <GenButton name="Select" onClick={selectMediaItem} highlighted small />
            <GenButton name="Download mp4" href={mediaItem.main_mp4_url} target="_blank" highlighted small />
            <GenButton name="Download Original" href={mediaItem.original_url} target="_blank" highlighted small />
          </div>
          <div>
            <div className="flex gap-2 mb-3 items-center">
              <h5>Original Captions</h5>
              {mediaItem.three_play_media_captions_processed_at ? (
                <>
                  <GenButton name="Download vtt" href={mediaItem.vtt_captions_url} target="_blank" highlighted small />
                  <GenButton name="Download srt" href={mediaItem.srt_captions_url} target="_blank" highlighted small />
                </>
                ) : (
                  <GenButton name="NO Original Captions" highlighted small disabled />
                )}
            </div>
            {
              SUBTITLE_LANGUAGES.map((subtitleLanguage) => (
                <div className="flex gap-2 mb-3 items-center" key={subtitleLanguage.lang}>
                  <h5>{subtitleLanguage.label}</h5>
                  {mediaItem.subtitle_file_by_language[subtitleLanguage.lang] ? (
                    <GenButton
                      name={`Download ${subtitleLanguage.label} subtitles`}
                      href={`${videoUrl}/${mediaItem.subtitle_file_by_language[subtitleLanguage.lang]}`}
                      target="_blank"
                      highlighted
                      small
                    />
                  ) : (
                    <GenButton
                      name={`No ${subtitleLanguage.label} Subtitles`}
                      highlighted
                      small
                      disabled
                    />
                  )}
                  <GenS3UploaderButton
                    name={`Upload ${subtitleLanguage.label} Subtitles`}
                    filePath={`${mediaItem.media_key}/`}
                    objectName={`subtitles-${subtitleLanguage.lang}.vtt`}
                    accept=".vtt,text/vtt"
                    onError={() => alert("Error Uploading File")}
                    onFinish={() => {
                      const subtitleFileByLanguage = mediaItem.subtitle_file_by_language;

                      subtitleFileByLanguage[subtitleLanguage.lang] = `subtitles-${subtitleLanguage.lang}.vtt`;

                      fetch(`/media_assets/${mediaItem.id}`, {
                        method: "PATCH",
                        body: JSON.stringify({"subtitle_file_by_language": subtitleFileByLanguage}),
                        headers: {
                          Accept: "application/json",
                          "Content-Type": "application/json",
                        },
                      })
                        .then((response) => {
                          if (!response.ok) { throw new Error(`${response.status} response`); }

                          return response.json();
                        })
                        .then(() => {
                          currentDocument._actions.read();
                        })
                        .catch(() => {});
                    }}
                  />
                </div>
              ))
            }
          </div>
          <hr className="my-6" />
        </div>
      );
    });
  } else {
    renderedMediaItems = (currentDocument[`${mediaType}s`] || []).map((mediaItem) => {
      const selectMediaItem = () => {
        if (openMenu.uid === "cover_image") {
          editState.setEditFieldsVersion({cover_image: mediaItem});
        } else {
          currentVersion._actions.modifyEditJSON((json) => setNodeValueByUIDAndKey(json, openMenu.uid, "uri", mediaItem));
        }
        displayState.setOpenMenu(undefined);
      };

      let preview;
      switch (mediaType) {
        case "audio":
          preview = <i className="fa fa-music" />;
          break;
        case "image":
          preview = <img src={`${currentVersion.aws_folder_url}/compressed/${mediaType}s/${mediaItem}`} />;
          break;
      }

      return (
        <div key={mediaItem}>
          {preview}
          <h6 style={{margin: "5px 0"}}>{mediaItem}</h6>
          <div className="flex gap-2">
            <GenButton name="Select" onClick={selectMediaItem} highlighted small />
          </div>
          <hr className="my-6" />
        </div>
      );
    });
  }

  let ultrasoundUploadButton;
  let extraBottomTools;
  if (mediaType === "video") {
    ultrasoundUploadButton = (
      <GenMediaUploadButton
        documentId={currentDocument.id as number} // TODO: Remove as
        mediaType={mediaType}
        editState={editState}
        onSuccess={() => currentDocument._actions.read()}
        titleEnding="Loop"
        mediaOptions={{is_silent: true}}
      />
    );

    extraBottomTools = (
      <GenMediaDuplicationTool
        documentId={currentDocument.id as number} // TODO: remove as
        onSuccess={() => currentDocument._actions.read()}
      />
    );
  }

  const deleteMediaFunction = () => {
    currentVersion._actions.modifyEditJSON((json) => deleteNodeByUID(json, openMenu.uid));
    displayState.setOpenMenu(undefined);
  };

  return (
    <GenModal open>
      <div className="gen-modal-panel">
        <div className="clearfix">
          <h3 style={{float: "left"}}>{capitalize(mediaType)}s</h3>
          <div style={{float: "right"}} className="flex gap-2">
            <GenButton
              name="Delete"
              onClick={deleteMediaFunction}
              highlighted
            />
            <GenMediaUploadButton
              documentId={currentDocument.id as number} // TODO: REMOVE as
              mediaType={mediaType}
              editState={editState}
              onSuccess={() => currentDocument._actions.read()}
            />
            {ultrasoundUploadButton}
            <GenButton name={<i className="fa fa-xmark" />} onClick={() => displayState.setOpenMenu(undefined)} highlighted />
          </div>
        </div>
        <hr className="my-6" />
        <div>
          {renderedMediaItems}
        </div>
        {extraBottomTools}
      </div>
    </GenModal>
  );
}
